import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

/**
 * Session-scoped mobile sidebar drawer state. Distinct from
 * `useSidebarCollapsed` (which is desktop-only width toggle persisted to
 * localStorage). The drawer auto-closes on route change + on Escape.
 */
interface MobileSidebarCtx {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const Ctx = createContext<MobileSidebarCtx | null>(null);

export function MobileSidebarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  // Lock background scroll while drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return <Ctx.Provider value={{ open, setOpen }}>{children}</Ctx.Provider>;
}

export function useMobileSidebar(): MobileSidebarCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMobileSidebar must be used inside MobileSidebarProvider');
  return ctx;
}
